import React, { useEffect, useState } from "react";
import { MapPin, Upload, X, AlertTriangle, Loader, Check } from "lucide-react";
import type { GeoStamp } from "../../services/api";

type GeoState =
  | { status: "idle" }
  | { status: "requesting" }
  | { status: "ok"; geo: GeoStamp }
  | { status: "error"; message: string };

export const useGeoCapture = (autoStart = true) => {
  const [state, setState] = useState<GeoState>({ status: "idle" });

  const capture = () => {
    if (!("geolocation" in navigator)) {
      setState({ status: "error", message: "Geolocation is not supported by this browser. Signing is blocked." });
      return;
    }
    setState({ status: "requesting" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          status: "ok",
          geo: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            capturedAt: new Date().toISOString(),
          },
        });
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. You must allow location access to sign."
            : err.code === err.POSITION_UNAVAILABLE
              ? "Location unavailable. Move to a spot with GPS / network and retry."
              : "Could not capture location. Please retry.";
        setState({ status: "error", message: msg });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  useEffect(() => {
    if (autoStart) capture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, capture };
};

export const GeoStatusBanner: React.FC<{ state: GeoState; onRetry: () => void }> = ({ state, onRetry }) => {
  if (state.status === "ok") {
    return (
      <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
        <MapPin className="h-4 w-4 shrink-0" />
        <span className="font-medium">Location captured</span>
        <span className="text-green-600 font-mono">
          {state.geo.lat.toFixed(5)}, {state.geo.lng.toFixed(5)}
        </span>
        {state.geo.accuracy != null && <span className="text-green-600">±{Math.round(state.geo.accuracy)}m</span>}
      </div>
    );
  }
  if (state.status === "requesting") {
    return (
      <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
        <Loader className="h-4 w-4 animate-spin" />
        Requesting your location…
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="flex items-start justify-between gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{state.message}</span>
        </div>
        <button type="button" onClick={onRetry} className="font-medium underline shrink-0">Retry</button>
      </div>
    );
  }
  return null;
};

export const FileDropzone: React.FC<{
  files: File[];
  onChange: (files: File[]) => void;
  label?: string;
  maxFiles?: number;
  disabled?: boolean;
}> = ({ files, onChange, label = "Proof photos / documents", maxFiles = 10, disabled }) => {
  const handleSelect = (selected: FileList | null) => {
    if (!selected) return;
    const next = [...files];
    Array.from(selected).forEach((f) => {
      if (next.length < maxFiles) next.push(f);
    });
    onChange(next);
  };
  const removeAt = (i: number) => {
    const next = files.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} <span className="text-xs text-gray-400">(optional, max {maxFiles})</span>
      </label>
      <label
        className={`block border-2 border-dashed rounded-lg px-4 py-4 text-center cursor-pointer transition ${
          disabled ? "bg-gray-100 border-gray-200 cursor-not-allowed" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
        }`}
      >
        <input
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          disabled={disabled}
          onChange={(e) => { handleSelect(e.target.files); e.currentTarget.value = ""; }}
        />
        <div className="flex flex-col items-center gap-1 text-xs text-gray-500">
          <Upload className="h-5 w-5" />
          <span>Tap to add photos or PDFs</span>
        </div>
      </label>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1">
              <div className="flex items-center gap-2 truncate">
                <Check className="h-3 w-3 text-green-600 shrink-0" />
                <span className="truncate">{f.name}</span>
                <span className="text-gray-400 shrink-0">({Math.round(f.size / 1024)} KB)</span>
              </div>
              <button type="button" onClick={() => removeAt(i)} className="text-red-500 hover:text-red-700">
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
